import React, {
  useEffect,
  useState,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  LayersControl,
  AttributionControl,
  ScaleControl,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { AppSettings } from "../../config/app-settings.js";
import useEvacuationStore from "../../stores/evacuation-store.js";
import useAuthStore from "../../stores/auth-store.js";
import { useAppSettings } from "../../hooks/useAuth.js";
import { getStyle, getZoneStatusStyle, customIcons } from "./utils/map-styles.js";
import {
  createMarkerClusterGroup,
  safeAddToCluster,
  safeAddToMap,
} from "./utils/map-clustering.js";
import {
  getLayerPane,
  calculateLabelSize,
  fitToFeature,
  fitBoundsToLayers,
} from "./utils/map-geometry.js";
import {
  createTooltipContent,
  setupEvacuationPolygonInteractions,
  setupPointInteractions,
  setupMusterStationInteractions,
  setupMusterStationPopup,
  setupLineInteractions,
  setupMapClickHandler,
} from "./utils/map-interactions.js";

// ==================== MAP CONFIGURATION ====================
const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY;

const tileLayers = {
  Positron: {
    url: `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attribution/">CARTO</a>',
    maxZoom: 20,
  },
  OSM: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 20,
  },
  Google: {
    url: "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Hybrid: {
    url: "https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Satellite: {
    url: "https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Terrain: {
    url: "https://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
    attribution: "&copy; Google",
    maxZoom: 20,
    subdomains: ["mt0", "mt1", "mt2", "mt3"],
  },
  Humanitarian: {
    url: "https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 20,
  },
  "Dark Matter": {
    url: `https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`,
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attribution/">CARTO</a>',
    maxZoom: 20,
  },
};

// ==================== LEAFLET ICON FIX ====================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ==================== MAP LAYER MANAGER ====================
const MapLayerManager = ({ pathname, clickable = false, onMapClick = null }) => {
  const map = useMap();
  const context = useContext(AppSettings);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [labelItems, setLabelItems] = useState([]);

  const mapRef = useRef(null);
  const activeLayersRef = useRef(new Set());
  const layerGroupsRef = useRef(new Map());

  const layerRegistry = useEvacuationStore((state) => state.layerRegistry);
  const visibleLayers = useEvacuationStore((state) => state.visibleLayers);
  const zonesData = useEvacuationStore((state) => state.zonesData);

  // Detect if we're in evacuation portal context
  const isReportPage = pathname === "evacuation-order-report";

  const initializeMapPanes = () => {
    if (!map) return;

    const panes = [
      { name: "polygonPane", zIndex: 200 },
      { name: "polygonPane2", zIndex: 250 },
      { name: "linePane", zIndex: 300 },
      { name: "pointPane", zIndex: 400 },
    ];

    panes.forEach(({ name, zIndex }) => {
      if (!map.getPane(name)) {
        map.createPane(name);
        map.getPane(name).style.zIndex = zIndex;
      }
    });
  };

  const processPointFeatures = (pointFeatures, layerKey, newLayerGroups) => {
    if (pointFeatures.length === 0) return;

    const isSidebarOpen = context.appSidebarEndToggled;

    // Detect if we're in evacuation portal context
    const isEvacuationPortal =
      pathname === "evacuation-portal" ||
      pathname === "evacuation-order-report" ||
      pathname === "ground-truth-buildings";

    // Helper: Check if feature is a muster station
    const isMusterStation = (feature) => {
      return isEvacuationPortal && feature.properties?.TAZ !== undefined;
    };

    // Separate muster stations from regular points
    const musterFeatures = pointFeatures.filter(isMusterStation);
    const regularFeatures = pointFeatures.filter((f) => !isMusterStation(f));

    console.log(`📍 Processing points for ${layerKey}:`, {
      total: pointFeatures.length,
      musterStations: musterFeatures.length,
      regularPoints: regularFeatures.length,
    });

    // ==================== PROCESS REGULAR POINTS ====================
    // For more than 1000 regular points, use clustering
    if (regularFeatures.length > 1000) {
      const markerClusterGroup = createMarkerClusterGroup(
        map,
        layerKey,
        regularFeatures.length
      );

      if (!markerClusterGroup) {
        console.warn(`Failed to create cluster group for ${layerKey}`);
        return;
      }

      let successfulMarkers = 0;

      regularFeatures.forEach((feature) => {
        try {
          const [lng, lat] = feature.geometry.coordinates;
          const pointStyle = getStyle("point", layerKey);

          const marker = L.circleMarker([lat, lng], {
            ...pointStyle,
            pane: "pointPane",
          });

          marker._getOriginalStyle = () => ({ ...pointStyle });
          // Only setup interactions if not on report page
          if (!isReportPage) {
            setupPointInteractions(marker, feature, map, isSidebarOpen, false);
          }

          const addedSuccessfully = safeAddToCluster(
            markerClusterGroup,
            marker,
            map,
            layerKey
          );
          if (addedSuccessfully) successfulMarkers++;
        } catch (error) {
          console.warn(`Error creating marker in ${layerKey}:`, error);
        }
      });

      if (successfulMarkers > 0) {
        const added = safeAddToMap(map, markerClusterGroup, layerKey);
        if (added) {
          newLayerGroups[`${layerKey}_clustered_points`] = markerClusterGroup;
          console.log(
            `✅ Added ${successfulMarkers} clustered points for ${layerKey}`
          );
        }
      }
    }

    // For 1000 regular points or fewer, add directly to map
    else if (regularFeatures.length > 0) {
      const regularGeoJSON = {
        type: "FeatureCollection",
        features: regularFeatures,
      };

      const regularLayer = L.geoJSON(regularGeoJSON, {
        pointToLayer: (feature, latlng) => {
          const pointStyle = getStyle("point", layerKey);
          const marker = L.circleMarker(latlng, {
            ...pointStyle,
            pane: "pointPane",
          });
          marker._getOriginalStyle = () => ({ ...pointStyle });
          return marker;
        },
        onEachFeature: (feature, layer) => {
          // Only setup interactions if not on report page
          if (!isReportPage) {
            setupPointInteractions(layer, feature, map, isSidebarOpen, false);
          }
        },
      });

      map.addLayer(regularLayer);
      newLayerGroups[`${layerKey}_individual_points`] = regularLayer;
      console.log(
        `✅ Added ${regularFeatures.length} individual points for ${layerKey}`
      );
    }

    // ==================== PROCESS MUSTER STATIONS ====================
    if (musterFeatures.length > 0) {
      const musterGeoJSON = {
        type: "FeatureCollection",
        features: musterFeatures,
      };

      // Use standard muster station name for styling
      const musterLayerKey = "Destinations_Muster_Stations";

      const musterLayer = L.geoJSON(musterGeoJSON, {
        pointToLayer: (feature, latlng) => {
          // Always use custom icon for muster stations
          const marker = L.marker(latlng, {
            icon: customIcons.musterStation,
            pane: "pointPane",
          });

          marker._getOriginalIcon = () => customIcons.musterStation;
          marker._getSelectedIcon = () => customIcons.musterStationSelected;

          return marker;
        },
        onEachFeature: (feature, layer) => {
          // Custom icon interactions for muster stations
          // Report page has no zone selection, but should still show the capacity tooltip on hover
          if (!isReportPage) {
            setupMusterStationInteractions(layer, feature, map, isSidebarOpen);
          } else {
            setupMusterStationPopup(layer, feature);
          }
        },
      });

      map.addLayer(musterLayer);

      // Store with descriptive key that includes original layerKey
      newLayerGroups[`${layerKey}`] = musterLayer;

      console.log(
        `✅ Added ${musterFeatures.length} muster station(s) for ${layerKey} (styled as ${musterLayerKey})`
      );
    }
  };

  const processLineFeatures = (lineFeatures, layerKey, newLayerGroups) => {
    if (lineFeatures.length === 0) return;

    // Detect if we're in evacuation portal context
    const isReportPage = pathname === "evacuation-order-report";

    const isSidebarOpen = context.appSidebarEndToggled;

    const lineGeoJSON = {
      type: "FeatureCollection",
      features: lineFeatures,
    };

    const lineLayer = L.geoJSON(lineGeoJSON, {
      style: () => getStyle("line", layerKey),
      pane: "linePane",
      onEachFeature: (feature, layer) => {
        layer._getOriginalStyle = () => getStyle("line", layerKey);
        // Only setup interactions if not on report page
        if (!isReportPage) {
          setupLineInteractions(layer, feature, map, isSidebarOpen);
        }
      },
    });

    map.addLayer(lineLayer);
    newLayerGroups[`${layerKey}_lines`] = lineLayer;
  };

  const processPolygonFeatures = (
    polygonFeatures,
    layerKey,
    newLayerGroups
  ) => {
    if (polygonFeatures.length === 0) return;

    // Detect if we're in evacuation portal context
    const isReportPage = pathname === "evacuation-order-report";

    // Look up this zone's order status (Is_Alerted/Is_Committed/Is_Rescinded) from the zonesData store
    const getZoneStatus = (properties) => {
      const zoneId = properties?.ZONE_ID || properties?.Zone_ID;
      if (!zoneId) return null;
      const zones = useEvacuationStore.getState().zonesData;
      return zones?.find((zone) => String(zone.Zone_ID) === String(zoneId)) || null;
    };

    const getZoneAreaStyle = (properties) =>
      getZoneStatusStyle(getStyle("area", layerKey), getZoneStatus(properties));

    const polygonGeoJSON = {
      type: "FeatureCollection",
      features: polygonFeatures,
    };

    const polygonLayer = L.geoJSON(polygonGeoJSON, {
      style: (feature) => getZoneAreaStyle(feature.properties),
      pane: getLayerPane(layerKey, polygonFeatures[0]),
      onEachFeature: (feature, layer) => {
        const featureData = {
          ...feature.properties,
          _layerFilename: layerKey,
          _timestamp: Date.now(),
        };

        layer.feature.properties = featureData;
        layer._isSelected = false;
        layer._getOriginalStyle = () => getZoneAreaStyle(layer.feature.properties);

        // Only setup interactions if not on report page
        if (!isReportPage) {
          setupEvacuationPolygonInteractions(
            layer,
            feature,
            map,
            layerKey,
            context
          );
        }
      },
    });

    map.addLayer(polygonLayer);
    newLayerGroups[`${layerKey}_polygons`] = polygonLayer;
  };

  const updateLabels = () => {
    if (!map) return;

    const currentZoom = map.getZoom();

    labelItems.forEach(({ marker, area, feature }) => {
      const fontSize = calculateLabelSize(area, currentZoom);

      if (fontSize > 0) {
        const newIcon = L.divIcon({
          className: "taz-label",
          html: `<div style="font-size: ${fontSize}px; font-weight: bold; color: black; text-shadow: 1px 1px 1px white;">${feature.properties.TAZ}</div>`,
          iconSize: [fontSize * 4, fontSize * 1.5],
          iconAnchor: [fontSize * 2, fontSize * 0.75],
        });
        marker.setIcon(newIcon);

        if (!map.hasLayer(marker)) {
          map.addLayer(marker);
        }
      } else {
        if (map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      }
    });
  };

  const clearAllLayers = useCallback(() => {
    if (!map) return;

    console.log("🗑️ CLEARING ALL LAYERS (unmount)");

    Object.values(layerGroupsRef.current).forEach((group) => {
      if (map.hasLayer(group)) {
        map.removeLayer(group);
      }
    });

    map.eachLayer((layer) => {
      if (layer instanceof L.GeoJSON || layer instanceof L.MarkerClusterGroup) {
        map.removeLayer(layer);
      }
    });

    labelItems.forEach(({ marker }) => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });

    activeLayersRef.current.clear();
  }, [map, labelItems]);

  const zoomToSelectionContext = useCallback(() => {
    if (!map) return;

    const { zoneLayer, routeLayer, musterStationLayer } =
      useEvacuationStore.getState().selectionContext;

    const selectionLayersMap = new Map();

    if (zoneLayer) selectionLayersMap.set("zone", zoneLayer);
    if (routeLayer) selectionLayersMap.set("route", routeLayer);
    if (musterStationLayer)
      selectionLayersMap.set("muster", musterStationLayer);

    if (selectionLayersMap.size === 0) {
      console.warn("⚠️ No selection context to zoom to");
      return;
    }

    console.log(
      `🎯 Zooming to selection context (${selectionLayersMap.size} layers)`
    );

    const isSidebarOpen = context.appSidebarEndToggled;

    fitBoundsToLayers(map, selectionLayersMap, isSidebarOpen, () => {
      console.log("✅ Zoomed to selection context");
    });
  }, [map, context.appSidebarEndToggled]);

  const findAndLinkMusterStation = useCallback(() => {
    const currentContext = useEvacuationStore.getState().selectionContext;

    if (!currentContext.musterCoordinates) {
      console.warn("⚠️ No muster coordinates to search for");
      return;
    }

    const musterStationsLayer = layerGroupsRef.current.get(
      "Destinations_Muster_Stations"
    );

    if (!musterStationsLayer) {
      console.warn("⚠️ Muster stations layer not found on map");
      return;
    }

    console.log("🔍 Searching for muster station in existing layer...");

    const { lat, lon } = currentContext.musterCoordinates;
    const { musterName } = currentContext;
    let musterStationMarker = null;

    musterStationsLayer.eachLayer((subLayer) => {
      // Clustered markers
      if (typeof subLayer.eachLayer === "function") {
        subLayer.eachLayer((marker) => {
          const feature = marker.feature;
          if (feature && feature.properties) {
            const featureLat = parseFloat(feature.properties.Latitude);
            const featureLon = parseFloat(feature.properties.Longitude);

            const roundedFeatureLat = Math.round(featureLat * 10000) / 10000;
            const roundedFeatureLon = Math.round(featureLon * 10000) / 10000;

            if (
              Math.abs(roundedFeatureLat - lat) < 0.0001 &&
              Math.abs(roundedFeatureLon - lon) < 0.0001
            ) {
              musterStationMarker = marker;
              console.log("✅ Found matching muster station!", {
                name: feature.properties["Building Name"],
                lat: roundedFeatureLat,
                lon: roundedFeatureLon,
              });
            } else if (
              musterName &&
              feature.properties["Building Name"].trim() === musterName
            ) {
              musterStationMarker = marker;
              console.log("✅ Found matching muster station by name!");
            }
          }
        });
      }

      // Direct marker (not in a group)
      else {
        const feature = subLayer.feature;
        if (feature && feature.properties) {
          const featureLat = parseFloat(feature.properties.Latitude);
          const featureLon = parseFloat(feature.properties.Longitude);

          const roundedFeatureLat = Math.round(featureLat * 10000) / 10000;
          const roundedFeatureLon = Math.round(featureLon * 10000) / 10000;

          if (
            Math.abs(roundedFeatureLat - lat) < 0.0001 &&
            Math.abs(roundedFeatureLon - lon) < 0.0001
          ) {
            musterStationMarker = subLayer;
            console.log("✅ Found matching muster station!");
          } else if (
            musterName &&
            feature.properties["Building Name"].trim() === musterName
          ) {
            musterStationMarker = subLayer;
            console.log("✅ Found matching muster station by name!");
          }
        }
      }
    });

    if (musterStationMarker) {
      useEvacuationStore.getState().updateSelectionContext({
        musterStationLayer: musterStationMarker,
      });

      setTimeout(() => {
        zoomToSelectionContext();
      }, 100);
    } else {
      console.warn("⚠️ Muster station not found at coordinates:", { lat, lon });
    }
  }, [map, zoomToSelectionContext]);

  const syncAllLayers = useCallback(() => {
    if (!map || !layerRegistry) return;

    const { dynamic: dynamicLayers, static: allStaticLayers } = layerRegistry;

    const staticLayers = Object.keys(allStaticLayers)
      .filter((key) => visibleLayers[key] !== false)
      .reduce((acc, key) => {
        acc[key] = allStaticLayers[key];
        return acc;
      }, {});

    console.log("🔄 Syncing layers:", {
      staticKeys: Object.keys(staticLayers),
      dynamicKeys: Object.keys(dynamicLayers),
      visibleLayersState: visibleLayers,
    });

    const shouldBeVisible = new Set([
      ...Object.keys(staticLayers),
      ...Object.keys(dynamicLayers),
    ]);

    console.log("📊 layerGroupsRef.current:", layerGroupsRef.current);
    const currentlyActive = activeLayersRef.current;

    const toAdd = [];
    const toRemove = [];

    shouldBeVisible.forEach((key) => {
      if (!currentlyActive.has(key)) toAdd.push(key);
    });

    currentlyActive.forEach((key) => {
      if (!shouldBeVisible.has(key)) toRemove.push(key);
    });

    console.log("📊 Layer diff:", { toAdd, toRemove });

    // Capture layer references BEFORE processing
    const layersToRemove = toRemove.map((key) => ({
      key,
      layerGroup: layerGroupsRef.current.get(key),
    }));
    console.log("🗑️ Layers to remove with references:", layersToRemove);

    // Remove layers (with captured references)
    layersToRemove.forEach(({ key, layerGroup }) => {
      console.log(`🗑️ Removing layer: ${key}`);

      if (layerGroup) {
        // If it's a FeatureGroup/LayerGroup, remove all child layers
        if (
          layerGroup.getLayers &&
          typeof layerGroup.getLayers === "function"
        ) {
          const childLayers = layerGroup.getLayers();
          console.log(`  └─ ${key} has ${childLayers.length} child layers`);

          childLayers.forEach((childLayer) => {
            if (map.hasLayer(childLayer)) {
              map.removeLayer(childLayer);
            }
          });

          console.log(
            `✅ Removed ${childLayers.length} child layers for ${key}`
          );
        }
        // For single layers
        else if (map.hasLayer(layerGroup)) {
          map.removeLayer(layerGroup);
          console.log(`✅ Removed single layer: ${key}`);
        }

        layerGroupsRef.current.delete(key);
      } else {
        console.warn(`⚠️ Layer ${key} not found in layerGroupsRef`);
      }

      activeLayersRef.current.delete(key);

      // Clean up labels
      setLabelItems((prev) =>
        prev.filter((item) => {
          const shouldRemove = item.feature.properties._layerFilename === key;
          if (shouldRemove && map.hasLayer(item.marker)) {
            map.removeLayer(item.marker);
          }
          return !shouldRemove;
        })
      );
    });

    // Add new layers
    if (toAdd.length > 0) {
      toAdd.forEach((key) => {
        console.log(`➕ Adding layer: ${key}`);

        const layerData = staticLayers[key] || dynamicLayers[key]?.data;
        const isDynamic = !!dynamicLayers[key];

        if (!layerData?.features?.length) {
          console.warn(`Cannot add layer ${key}: no features`);
          return;
        }

        try {
          const pointFeatures = layerData.features.filter(
            (f) => f.geometry?.type === "Point"
          );
          const lineFeatures = layerData.features.filter(
            (f) =>
              f.geometry?.type === "LineString" ||
              f.geometry?.type === "MultiLineString"
          );
          const polygonFeatures = layerData.features.filter(
            (f) =>
              f.geometry?.type === "Polygon" ||
              f.geometry?.type === "MultiPolygon"
          );

          const tempLayerGroups = {};

          if (polygonFeatures.length > 0) {
            processPolygonFeatures(polygonFeatures, key, tempLayerGroups);
          }
          if (lineFeatures.length > 0) {
            processLineFeatures(lineFeatures, key, tempLayerGroups);
          }
          if (pointFeatures.length > 0) {
            processPointFeatures(pointFeatures, key, tempLayerGroups);
          }

          const allSubGroups = Object.values(tempLayerGroups);

          if (allSubGroups.length > 0) {
            let layerToStore;

            if (allSubGroups.length === 1) {
              layerToStore = allSubGroups[0];
              console.log(`✅ Stored single group for ${key}`);
            } else {
              layerToStore = L.featureGroup(allSubGroups);
              console.log(
                `✅ Combined ${allSubGroups.length} sub-groups for ${key}`
              );
            }

            layerGroupsRef.current.set(key, layerToStore);
            activeLayersRef.current.add(key);

            if (!isReportPage && isDynamic) {
              const currentContext =
                useEvacuationStore.getState().selectionContext;
              const metadata = dynamicLayers[key];

              if (metadata.linkedTo === currentContext.zoneId) {
                if (lineFeatures.length > 0) {
                  useEvacuationStore.getState().updateSelectionContext({
                    routeLayer: layerToStore,
                  });
                  console.log("✅ Route layer linked to selection context");
                }
              }
            }
          }
        } catch (error) {
          console.error(`Error processing layer ${key}:`, error);
        }
      });

      // After additions, fit bounds if initial load or report page
      if (isInitialLoad || isReportPage) {
        const allGroups = Array.from(layerGroupsRef.current.values());
        if (allGroups.length > 0) {
          const isSidebarOpen = context.appSidebarEndToggled;
          console.log("🎯 Fitting bounds to layers:", {
            isInitialLoad,
            isReportPage,
            layerCount: allGroups.length,
          });
          fitBoundsToLayers(map, layerGroupsRef.current, isSidebarOpen, () => {
            if (isInitialLoad) {
              setIsInitialLoad(false);
            }
            console.log("✅ Map bounds fitted");
          });
        }
      }

      // After sync, check and link muster station only for evacuation portal
      if (!isReportPage) {
        const currentContext = useEvacuationStore.getState().selectionContext;
        if (
          currentContext.musterCoordinates &&
          !currentContext.musterStationLayer
        ) {
          findAndLinkMusterStation();
        }
      }
    }
  }, [
    map,
    layerRegistry,
    visibleLayers,
    context.appSidebarEndToggled,
    isInitialLoad,
    isReportPage,
  ]);

  // ==================== EFFECTS ====================
  // Handle map ref, panes initialization and cleanup
  useEffect(() => {
    if (!map) return;

    mapRef.current = map;
    initializeMapPanes();

    const handleZoom = () => updateLabels();
    map.on("zoomend", handleZoom);

    const timer = setTimeout(() => {
      map.invalidateSize();
      console.log("🗺️ Map size invalidated");
    }, 200);

    return () => {
      if (map) {
        console.log("🧹 Map unmounting - clearing all store layers and state");

        map.off("zoomend");
        clearAllLayers();
        clearTimeout(timer);

        // useEvacuationStore.getState().clearAll();
        console.log("Map layers cleanup complete");
      }
    };
  }, [map]);

  // Sync layers when registry or visibility changes
  useEffect(() => {
    syncAllLayers();
  }, [syncAllLayers]);

  // Setup map click handler for custom interactions
  useEffect(() => {
    if (!map) return;
    
    const cleanup = setupMapClickHandler(map, clickable, onMapClick);
    
    return cleanup;
  }, [map, clickable, onMapClick]);

  // Re-apply zone colors once status data has (re)loaded, since polygons may render before it arrives
  useEffect(() => {
    const restyleIfUnselected = (layer) => {
      if (typeof layer.eachLayer === "function") {
        layer.eachLayer(restyleIfUnselected);
        return;
      }
      if (!layer._isSelected && typeof layer._getOriginalStyle === "function" && typeof layer.setStyle === "function") {
        layer.setStyle(layer._getOriginalStyle());
      }
    };

    layerGroupsRef.current.forEach((layerGroup) => {
      if (layerGroup) restyleIfUnselected(layerGroup);
    });
  }, [zonesData]);

  return null;
};

// ==================== MAP CLICK HANDLER ====================
// ==================== MAIN MAP COMPONENT ====================
const MapComponent = ({ center, zoom, height = "100%", width = "100%", clickable = false, onMapClick = null }) => {
  const location = useLocation();
  const pathname = location.pathname.split("/")[2] || "default";

  // Determine which app settings to fetch based on current route
  // Routes like "evacuation-portal", "model-mapping", etc. map to app_name in DB
  const appName = pathname === "default" ? "evacuation-portal" : pathname;
  
  // Fetch settings for current app
  const { data: settingsData, isLoading: settingsLoading, error: settingsError } = useAppSettings(appName);
  
  // Get map settings from auth store (may legitimately be absent if no client/app override or app-wide default exists - fall back to defaults below)
  const mapSettings = useAuthStore((state) => state.getSettingCategory(appName, 'map'));

  // Show loading state only while the settings request is in flight
  if (settingsLoading) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading map settings...</span>
          </div>
          <p className="text-muted">Loading map configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (settingsError) {
    return (
      <div style={{ height, width, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff3cd' }}>
        <div className="text-center p-4">
          <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5>Failed to Load Map Settings</h5>
          <p className="text-muted">{settingsError.message || 'Could not retrieve map configuration from server'}</p>
        </div>
      </div>
    );
  }

  // Extract map settings from flat structure, falling back to defaults when no 'map' category exists
  const mapCenter = center || mapSettings?.map_center || [49.8844, -119.4859];
  const mapZoom = zoom || mapSettings?.map_zoom || 9;
  const defaultLayer = mapSettings?.map_layer || "Hybrid";

  return (
    <div style={{ height, width }}>
      <MapContainer
        className="grid-map-print"
        center={mapCenter}
        zoom={mapZoom}
        zoomDelta={0.25}
        zoomSnap={0}
        style={{ height: "100%", width: "100%" }}
      >
        <LayersControl position="topleft">
          {Object.entries(tileLayers).map(([name, config]) => (
            <LayersControl.BaseLayer
              key={name}
              checked={name === defaultLayer}
              name={name}
            >
              <TileLayer {...config} />
            </LayersControl.BaseLayer>
          ))}
        </LayersControl>

        <AttributionControl
          prefix='Vector data &copy; <a href="https://www.acuere.ca">Acuere Consulting Inc.</a>'
          position="bottomleft"
        />

        <ScaleControl maxWidth={150} position="bottomleft" />

        <MapLayerManager pathname={pathname} clickable={clickable} onMapClick={onMapClick} />
      </MapContainer>
    </div>
  );
};

export default MapComponent;
